import { useState, useEffect, useCallback } from 'react'
import {
  Search,
  RefreshCw,
  AlertTriangle,
  Lightbulb,
  Copy,
  Target,
  Loader2,
  ChevronRight,
  ChevronDown,
  MessageSquare,
  ThumbsUp,
  BarChart3,
} from 'lucide-react'
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts'
import {
  getAISummary,
  generateAISummary,
  type AISummaryResponse,
  type AIPainPoint,
  type AIFeatureRequest,
  type AIDuplicate,
  type AIPriority,
} from '../../lib/feedbackApi'
import { formatRelativeTime } from '../../lib/format'
import StatCard from '../analytics/StatCard'

// Chart colors matching existing TYPE_BADGE and STATUS_BADGE semantics
const TYPE_COLORS: Record<string, string> = {
  bug: 'hsl(0, 84%, 60%)',
  feature: 'hsl(217, 91%, 60%)',
  improvement: 'hsl(160, 84%, 39%)',
  integration: 'hsl(262, 83%, 58%)',
}

const STATUS_COLORS: Record<string, string> = {
  backlog: 'hsl(215, 16%, 47%)',
  in_progress: 'hsl(38, 92%, 50%)',
  done: 'hsl(160, 84%, 39%)',
  dismissed: 'hsl(0, 84%, 60%)',
}

// Severity/priority badge styles (per UI-SPEC Badge Specifications)
const SEVERITY_BADGE: Record<string, { bg: string; text: string }> = {
  critical: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400' },
  high: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400' },
  medium: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400' },
  low: { bg: 'bg-secondary', text: 'text-muted-foreground' },
}

// Recharts tooltip style matching TrendChart.tsx
const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(var(--card))',
  borderColor: 'hsl(var(--border))',
  borderRadius: '0.5rem',
  color: 'hsl(var(--foreground))',
}

interface FeedbackInsightsProps {
  onItemSelect: (id: number) => void
  cachedData?: AISummaryResponse | null
  onDataChange?: (data: AISummaryResponse | null) => void
}

export function FeedbackInsights({ onItemSelect, cachedData, onDataChange }: FeedbackInsightsProps) {
  const [data, setData] = useState<AISummaryResponse | null>(cachedData ?? null)
  const [loading, setLoading] = useState(cachedData === undefined)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [aiNotConfigured, setAiNotConfigured] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    pain_points: true,
    feature_requests: true,
    potential_duplicates: false,
    suggested_priorities: true,
  })

  // Notify parent of data changes for persistence across tab switches
  const updateData = useCallback((newData: AISummaryResponse | null) => {
    setData(newData)
    onDataChange?.(newData)
  }, [onDataChange])

  useEffect(() => {
    // Skip fetch if we already have cached data from parent
    if (cachedData !== undefined) return

    let cancelled = false
    async function fetchSummary() {
      try {
        const result = await getAISummary()
        if (!cancelled) {
          updateData(result)
          setLoading(false)
        }
      } catch (err: unknown) {
        if (cancelled) return
        const status = (err as { status?: number }).status
        if (status === 404) {
          // No summary yet -- show empty state
          setLoading(false)
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load AI summary')
          setLoading(false)
        }
      }
    }
    fetchSummary()
    return () => { cancelled = true }
    // Mount-only fetch; cachedData/updateData intentionally omitted to avoid refetch on parent state change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGenerate = useCallback(async () => {
    setGenerating(true)
    setError(null)
    setAiNotConfigured(false)
    try {
      const result = await generateAISummary()
      updateData(result)
    } catch (err: unknown) {
      const status = (err as { status?: number }).status
      if (status === 503) {
        setAiNotConfigured(true)
      } else {
        setError(err instanceof Error ? err.message : 'Failed to generate AI summary')
      }
    } finally {
      setGenerating(false)
    }
  }, [updateData])

  const toggleSection = useCallback((key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16" aria-busy="true">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // AI not configured state
  if (aiNotConfigured) {
    return (
      <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-foreground">AI features not available</p>
          <p className="text-sm text-muted-foreground mt-1">
            AI features require the Gemini API key to be configured. Contact your administrator.
          </p>
        </div>
      </div>
    )
  }

  // Error state
  if (error && !data) {
    return (
      <div className="border border-red-500/30 bg-red-500/5 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-foreground">Error loading insights</p>
          <p className="text-sm text-muted-foreground mt-1">{error}</p>
        </div>
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="px-3 py-1.5 text-sm font-medium border border-border rounded-lg hover:bg-secondary transition-colors"
        >
          Try Again
        </button>
      </div>
    )
  }

  // Empty state (no summary yet)
  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Search className="w-12 h-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-semibold mb-1">No AI insights yet</h3>
        <p className="text-sm text-muted-foreground max-w-md mb-6">
          Generate an AI-powered analysis of all feedback to surface pain points, feature requests, potential duplicates, and suggested priorities.
        </p>
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Analyzing feedback...
            </>
          ) : (
            <>
              <Search className="w-4 h-4" />
              Generate Insights
            </>
          )}
        </button>
      </div>
    )
  }

  // Summary loaded state — defensive checks to prevent white page on unexpected AI response
  const summary = data.summary
  if (!summary) {
    return (
      <div className="border border-red-500/30 bg-red-500/5 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-foreground">Invalid summary data</p>
          <p className="text-sm text-muted-foreground mt-1">The AI returned an unexpected response format. Try generating again.</p>
        </div>
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="px-3 py-1.5 text-sm font-medium border border-border rounded-lg hover:bg-secondary transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }
  const stats = summary.stats || {
    by_type: {} as Record<string, number>,
    by_status: {} as Record<string, number>,
    total_items: 0,
    total_votes: 0,
    avg_votes_per_item: 0,
  }

  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Last updated: {formatRelativeTime(data.generated_at)}
          </p>
          {data.is_stale && data.new_items_count > 0 && (
            <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
              {data.new_items_count} new item{data.new_items_count !== 1 ? 's' : ''} since last analysis
            </p>
          )}
        </div>
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium border border-border rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
        >
          {generating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Refresh Insights
        </button>
      </div>

      {/* Error banner (non-blocking, when data exists but refresh fails) */}
      {error && (
        <div className="border border-red-500/30 bg-red-500/5 rounded-lg p-3 flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
          <span className="text-red-600 dark:text-red-400">{error}</span>
        </div>
      )}

      {/* Executive Brief Card */}
      <div className="border border-border rounded-lg p-4 bg-card">
        <p className="text-sm text-foreground leading-relaxed">{summary.executive_brief}</p>
      </div>

      {/* Stat Cards Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total Items" value={stats.total_items ?? 0} icon={MessageSquare} />
        <StatCard label="Total Votes" value={stats.total_votes ?? 0} icon={ThumbsUp} />
        <StatCard label="Avg Votes/Item" value={(stats.avg_votes_per_item ?? 0).toFixed(1)} icon={BarChart3} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Type Distribution Pie Chart */}
        <div className="border border-border rounded-xl bg-card p-4">
          <h3 className="text-sm font-semibold mb-3">Type Distribution</h3>
          {Object.keys(stats.by_type || {}).length === 0 ? (
            <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height: 240 }}>
              No data
            </div>
          ) : (
            <div style={{ height: 240 }} aria-label="Pie chart showing feedback type distribution">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={Object.entries(stats.by_type || {}).map(([name, value]) => ({ name, value }))}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    label={({ name, percent }: { name?: string; percent?: number }) => `${name ?? ''} ${((percent ?? 0) * 100).toFixed(0)}%`}
                  >
                    {Object.keys(stats.by_type || {}).map((key) => (
                      <Cell key={key} fill={TYPE_COLORS[key] || 'hsl(215, 16%, 47%)'} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Status Breakdown Bar Chart */}
        <div className="border border-border rounded-xl bg-card p-4">
          <h3 className="text-sm font-semibold mb-3">Status Breakdown</h3>
          {Object.keys(stats.by_status || {}).length === 0 ? (
            <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height: 240 }}>
              No data
            </div>
          ) : (
            <div style={{ height: 240 }} aria-label="Bar chart showing feedback status breakdown">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={Object.entries(stats.by_status || {}).map(([name, value]) => ({ name: name.replace('_', ' '), value, key: name }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                  <YAxis tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="value">
                    {Object.entries(stats.by_status || {}).map(([key]) => (
                      <Cell key={key} fill={STATUS_COLORS[key] || 'hsl(215, 16%, 47%)'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Structured Sections */}
      <div className="space-y-4">
        <CollapsibleSection
          sectionKey="pain_points"
          title="Top Pain Points"
          icon={AlertTriangle}
          items={summary.pain_points || []}
          expanded={expandedSections.pain_points}
          onToggle={toggleSection}
          renderItem={(item: AIPainPoint) => (
            <PainPointRow item={item} onItemSelect={onItemSelect} />
          )}
        />

        <CollapsibleSection
          sectionKey="feature_requests"
          title="Most Requested Features"
          icon={Lightbulb}
          items={summary.feature_requests || []}
          expanded={expandedSections.feature_requests}
          onToggle={toggleSection}
          renderItem={(item: AIFeatureRequest) => (
            <FeatureRequestRow item={item} onItemSelect={onItemSelect} />
          )}
        />

        <CollapsibleSection
          sectionKey="potential_duplicates"
          title="Potential Duplicates"
          icon={Copy}
          items={summary.potential_duplicates || []}
          expanded={expandedSections.potential_duplicates}
          onToggle={toggleSection}
          renderItem={(item: AIDuplicate) => (
            <DuplicateRow item={item} onItemSelect={onItemSelect} />
          )}
        />

        <CollapsibleSection
          sectionKey="suggested_priorities"
          title="Suggested Priorities"
          icon={Target}
          items={summary.suggested_priorities || []}
          expanded={expandedSections.suggested_priorities}
          onToggle={toggleSection}
          renderItem={(item: AIPriority) => (
            <PriorityRow item={item} onItemSelect={onItemSelect} />
          )}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Collapsible section wrapper
// ---------------------------------------------------------------------------

interface CollapsibleSectionProps<T> {
  sectionKey: string
  title: string
  icon: React.ElementType
  items: T[]
  expanded: boolean
  onToggle: (key: string) => void
  renderItem: (item: T, index: number) => React.ReactNode
}

function CollapsibleSection<T>({
  sectionKey,
  title,
  icon: SectionIcon,
  items,
  expanded,
  onToggle,
  renderItem,
}: CollapsibleSectionProps<T>) {
  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      <button
        onClick={() => onToggle(sectionKey)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-secondary/30 cursor-pointer hover:bg-secondary/50 transition-colors"
        aria-expanded={expanded}
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <SectionIcon className="w-4 h-4 text-muted-foreground" />
        <span className="text-base font-semibold">{title}</span>
        <span className="text-xs text-muted-foreground ml-auto">{items.length} items</span>
      </button>
      {expanded && (
        <div className="divide-y divide-border">
          {items.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground italic">None identified</div>
          ) : (
            items.map((item, i) => renderItem(item, i))
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Feedback ID links helper
// ---------------------------------------------------------------------------

function FeedbackIdLinks({
  ids,
  onItemSelect,
}: {
  ids: number[] | undefined
  onItemSelect: (id: number) => void
}) {
  if (!ids || ids.length === 0) return null
  return (
    <span className="inline-flex flex-wrap gap-1">
      {ids.map((id) => (
        <button
          key={id}
          onClick={(e) => { e.stopPropagation(); onItemSelect(id) }}
          className="text-xs text-primary hover:underline"
        >
          #{id}
        </button>
      ))}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Section item rows
// ---------------------------------------------------------------------------

function PainPointRow({ item, onItemSelect }: { item: AIPainPoint; onItemSelect: (id: number) => void }) {
  const badge = SEVERITY_BADGE[item.severity] || SEVERITY_BADGE.medium
  return (
    <div className="px-4 py-3 space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{item.title}</span>
        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full ${badge.bg} ${badge.text}`}>
          {item.severity}
        </span>
      </div>
      {item.description && (
        <p className="text-xs text-muted-foreground">
          {item.description.length > 100 ? `${item.description.slice(0, 100)}...` : item.description}
        </p>
      )}
      <FeedbackIdLinks ids={item.feedback_ids} onItemSelect={onItemSelect} />
    </div>
  )
}

function FeatureRequestRow({ item, onItemSelect }: { item: AIFeatureRequest; onItemSelect: (id: number) => void }) {
  return (
    <div className="px-4 py-3 space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{item.title}</span>
        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full bg-primary/10 text-primary">
          {item.vote_count} votes
        </span>
      </div>
      {item.description && (
        <p className="text-xs text-muted-foreground">
          {item.description.length > 100 ? `${item.description.slice(0, 100)}...` : item.description}
        </p>
      )}
      <FeedbackIdLinks ids={item.feedback_ids} onItemSelect={onItemSelect} />
    </div>
  )
}

function DuplicateRow({ item, onItemSelect }: { item: AIDuplicate; onItemSelect: (id: number) => void }) {
  return (
    <div className="px-4 py-3 space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{item.group_title}</span>
        <FeedbackIdLinks ids={item.feedback_ids} onItemSelect={onItemSelect} />
      </div>
      <p className="text-xs text-muted-foreground italic">{item.similarity_reason}</p>
    </div>
  )
}

function PriorityRow({ item, onItemSelect }: { item: AIPriority; onItemSelect: (id: number) => void }) {
  const badge = SEVERITY_BADGE[item.priority] || SEVERITY_BADGE.medium
  return (
    <div className="px-4 py-3 space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{item.title}</span>
        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full ${badge.bg} ${badge.text}`}>
          {item.priority}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">{item.rationale}</p>
      <FeedbackIdLinks ids={item.feedback_ids} onItemSelect={onItemSelect} />
    </div>
  )
}
